/**
 * What the bundled EPA Green Book NAYRO export covers, for readers of the
 * persisted designation block (`environmental.data.naaqsDesignations`).
 *
 * 'not-listed' means no current nonattainment or maintenance designation
 * listed as of the export date, under the standards the export includes. A
 * designation under a standard the export does not include (PM-2.5 (2024) in
 * the 2026-08-31 export), or one made after the export, is outside it: the
 * listing can neither confirm nor rule it out.
 *
 * Imports only the table's ~3 KB provenance header, never the table itself,
 * so client code can use it (see `epaDesignation.ts`).
 */
import type { EpaAttainmentData, EpaNonattainmentMeta } from '../../services/dataProviders/epaAttainment';
import epaNonattainmentTableMeta from '../../data/epaOzoneNonAttainment.meta.json';
import { epaDesignationAsOf, nonattainmentClaimSegments, readEpaDesignation } from './epaDesignation';

const META = epaNonattainmentTableMeta as unknown as EpaNonattainmentMeta;

/** Coverage id of the bundled table, e.g. 'green-book-nayro-2026-08-31' (same value as `EPA_NAYRO_COVERAGE`). */
export const EPA_TABLE_COVERAGE: string = META.coverage;

const ISO_DATE = /(\d{4}-\d{2}-\d{2})$/;

/**
 * F5 staleness rule: true when a persisted designation block was produced
 * from an older Green Book export than the one shipped (`shippedCoverage`,
 * default the bundled table), so the next top-up should refresh it. A block
 * without `coverage` (the pre-table stub) or with an unparsable one is stale;
 * a block from the shipped export or a newer one is not. Absent blocks are
 * not stale: there is nothing to refresh, and whether to fill them is a
 * separate decision. Readers keep trusting a stale block until it is
 * replaced (`readEpaDesignation` does not look at the vintage), so a new
 * export never silently drops every site to the text fallback.
 */
export function epaDesignationPredates(
  epa: EpaAttainmentData | null | undefined,
  shippedCoverage: string = EPA_TABLE_COVERAGE,
): boolean {
  if (!epa) return false;
  const stored = epa.coverage ? ISO_DATE.exec(epa.coverage)?.[1] : undefined;
  const shipped = ISO_DATE.exec(shippedCoverage)?.[1];
  if (!stored) return true;
  return !!shipped && stored < shipped;
}

/** A nonattainment claim in research text that the export behind a listing cannot speak to. */
export interface EpaExportGap {
  /**
   * The standard in Green Book spelling ("PM-2.5 (2024)") when the text names
   * one the export does not include, or "a designation dated <year>" when the
   * text dates a designation after the export.
   */
  standard: string;
  reason: 'not-in-export' | 'newer-than-export';
}

const YEAR = String.raw`(?:19|20)\d{2}`;
const POLLUTANT = String.raw`(?:(?:(?:8|1|one|eight)[- ]hour\s+)?ozone|O3|PM[- ]?2\.5|fine\s+particulate(?:\s+matter)?|PM[- ]?10|SO2|sulph?ur\s+dioxide|NO2|nitrogen\s+dioxide|CO|carbon\s+monoxide|lead|Pb)`;
const QUALIFIER = String.raw`(?:annual|24[- ]hour|daily|primary|secondary|revised|final|(?:8|1|one|eight)[- ]hour)`;
/** "2024 annual PM2.5", "2015 8-hour ozone", "the 1997 ozone standard". */
const YEAR_THEN_POLLUTANT = new RegExp(String.raw`\b(${YEAR})(?:\s+${QUALIFIER})*\s+(${POLLUTANT})(?:\s+(?:NAAQS|standards?|non-?attainment))?\b`, 'gi');
/** "PM2.5 (2024)", "ozone standard (2015)", "PM-2.5 2024 standard". */
const POLLUTANT_THEN_YEAR = new RegExp(String.raw`\b(${POLLUTANT})(?:\s+(?:NAAQS|standard))?\s*(?:\(\s*(${YEAR})\s*\)|\s(${YEAR})\b)`, 'gi');
/** A designation or reclassification dated in the text: "designated nonattainment effective 2027". */
const DATED_DESIGNATION = new RegExp(String.raw`\b(?:designat\w*|effective|reclassif\w*)(?:\s+[^\s.;!?]+){0,5}?\s+\(?(${YEAR})\b`, 'gi');
/** An attainment date or deadline ("attainment date 2027", "must attain by 2027") dates no designation. */
const ATTAINMENT_DATE = /(?<!non-)\battain/i;
/** The mention calls itself a standard ("the 2027 ozone standard", "the 2024 annual PM2.5 NAAQS"). */
const NAMED_AS_STANDARD = /\b(?:standards?|NAAQS)\b/i;
/**
 * NAAQS that EPA has promulgated (40 CFR Part 50) but the 2026-08-31 export
 * does not list: PM-2.5 (2024) and Nitrogen Dioxide (2010). With the export's
 * included and revoked standards they are the years a mention needs no
 * "standard" or "NAAQS" for ("2024 annual PM2.5").
 */
const PROMULGATED_NOT_IN_EXPORT: readonly string[] = ['PM-2.5 (2024)', 'Nitrogen Dioxide (2010)'];
const KNOWN_STANDARDS = new Set([...META.naaqsIncluded, ...META.revokedNaaqsExcluded, ...PROMULGATED_NOT_IN_EXPORT]);

/** Green Book label for a pollutant mention and a standard year, or null when the mention is not a pollutant. */
function greenBookLabel(pollutant: string, year: string, context: string): string | null {
  if (/^CO$/i.test(pollutant) && pollutant !== 'CO') return null; // "co-" is not carbon monoxide
  const p = pollutant.toLowerCase();
  // "lead" is also a verb ("designations in 2019 lead to offsets"): only the pollutant with its standard.
  if (p === 'lead' && !/\b(?:NAAQS|standards?|non-?attainment)\b|\(/i.test(context)) return null;
  if (/ozone|^o3$/.test(p)) return /\b(?:1|one)[- ]hour\b/i.test(context) ? `1-Hour Ozone (${year})` : `8-Hour Ozone (${year})`;
  if (/2\.5|fine/.test(p)) return `PM-2.5 (${year})`;
  if (/10$/.test(p)) return `PM-10 (${year})`;
  if (/so2|sulph?ur/.test(p)) return `Sulfur Dioxide (${year})`;
  if (/no2|nitrogen/.test(p)) return `Nitrogen Dioxide (${year})`;
  if (/^co$|carbon/.test(p)) return `Carbon Monoxide (${year})`;
  return `Lead (${year})`;
}

/**
 * The standards (and designation dates) a research-text nonattainment claim
 * names that the export behind a coverage-backed `epa` block cannot confirm or
 * rule out. Callers read it where the listing disagrees with the claim (the
 * `epa-attainment-fips` cross-check, the Stage 2 disagreement reason). Only
 * the clauses that still state nonattainment once negations are removed
 * count. A standard counts as covered when the export includes it or excludes
 * it as revoked (1-Hour Ozone (1979), 8-Hour Ozone (1997)): a revoked-standard
 * claim is the case the listing correctly overrides. For a block from another
 * export than the bundled one only the years are checked, against its own
 * export date. Text naming no year adds nothing.
 *
 * A year beside a pollutant names a standard only in Green Book form ("PM2.5
 * (2024)"), beside "standard" or "NAAQS" ("the 2027 ozone standard"), or when
 * it is the year of a known standard (`KNOWN_STANDARDS`, "2024 annual PM2.5"):
 * "EPA's 2018 ozone designations" names no "8-Hour Ozone (2018)". A year after
 * attainment wording ("attainment date 2027") dates no designation.
 */
export function epaExportGaps(
  epa: EpaAttainmentData | null | undefined,
  researchText: string | null | undefined,
): EpaExportGap[] {
  const asOf = epaDesignationAsOf(epa);
  if (readEpaDesignation(epa) === 'not-assessed' || !asOf) return [];
  const exportYear = Number(asOf.slice(0, 4));
  const covered = epa!.coverage === META.coverage
    ? new Set([...META.naaqsIncluded, ...META.revokedNaaqsExcluded])
    : null;
  const gaps = new Map<string, EpaExportGap>();
  const add = (gap: EpaExportGap): void => { if (!gaps.has(gap.standard)) gaps.set(gap.standard, gap); };
  const standard = (pollutant: string, year: string, context: string, namedAsStandard: boolean): void => {
    const label = greenBookLabel(pollutant, year, context);
    if (!label || (!namedAsStandard && !KNOWN_STANDARDS.has(label))) return;
    if (covered ? !covered.has(label) : Number(year) > exportYear) {
      add({ standard: label, reason: covered ? 'not-in-export' : 'newer-than-export' });
    }
  };
  /** The match, or the word right after it ("PM-2.5 2024 standard"), calls it a standard. */
  const callsItStandard = (segment: string, m: RegExpMatchArray): boolean =>
    NAMED_AS_STANDARD.test(m[0]) || /^\s+(?:standards?|NAAQS)\b/i.test(segment.slice(m.index! + m[0].length));
  for (const segment of nonattainmentClaimSegments(researchText)) {
    for (const m of segment.matchAll(YEAR_THEN_POLLUTANT)) standard(m[2]!, m[1]!, m[0], callsItStandard(segment, m));
    for (const m of segment.matchAll(POLLUTANT_THEN_YEAR)) standard(m[1]!, (m[2] ?? m[3])!, m[0], m[2] !== undefined || callsItStandard(segment, m));
    for (const m of segment.matchAll(DATED_DESIGNATION)) {
      if (Number(m[1]) > exportYear && !ATTAINMENT_DATE.test(m[0])) add({ standard: `a designation dated ${m[1]}`, reason: 'newer-than-export' });
    }
  }
  return [...gaps.values()];
}

/** "PM-2.5 (2024)", "PM-2.5 (2024) and a designation dated 2027". */
export function describeEpaExportGaps(gaps: readonly EpaExportGap[]): string {
  const names = gaps.map(gap => gap.standard);
  return names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
